import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { LineItem } from 'shared/types/cart';
import CuratedCtaCard from 'components/commercetools-ui/molecules/curated-cta-card';
import Tile, { Props as TileProps } from 'components/commercetools-ui/molecules/curated-tile';
import useOrderFetchFilter from 'components/commercetools-ui/organisms/account/sections/orders/helper-hooks/useOrderFetchFilters';
import { fontStyleMap, positionMap } from 'helpers/constants';
import { classifyCartKind } from 'helpers/constants/cart';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useRefreshImages from 'helpers/hooks/useRefreshImages';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import { desktop, mediumDesktop, mobile, smallMobile, tablet } from 'helpers/utils/screensizes';
import { cleanString } from 'helpers/utils/util';
import { useRouter } from 'i18n/routing';
import { Order } from 'types/entity/order';
import Slider from '../../atoms/slider';
import { getLeastOrderStatus } from '../account/sections/orders/order-page/components/OrderUtils';
import { OrderStatusConfig } from '../account/types/types';
import { setFFIDsForPagination } from '../curated-slider/helpers';
import { CtaCardLabel, CtaCardText } from '../curated-slider/types';
import { useUserName } from '../utils';

export interface PastOrderProps {
  mainHeading?: string;
  subHeading?: string;
  expiresInOrStatus?: string;
  tileCtaText?: string;
  ctaCardLabel?: CtaCardLabel[] | [];
  ctaText?: CtaCardText[];
  mainHeadingTextColor?: string;
  ariaLabelOfPreviousButton?: string;
  ariaLabelOfNextButton?: string;
  mainHeadingFontweight?: string;
  mainHeadingFontWeightMobile?: string;
  mainHeadingPosition?: string;
  mainHeadingFontsizeMobile?: number;
  subtextFontsize?: number;
  mainHeadingFontsize?: number;
  subtextHeadingFontweight?: string;
  subtextHeadingTextColor?: string;
  subtextHeadingFontsizeMobile?: number;
  tileTitleFontWeight?: string;
  tileTextFontSize?: number;
  autoRotation?: boolean;
  autoRotationSpeed?: number;
  expiresInOrStatusFontweight?: string;
  expiresInOrStatusBgColor?: string;
  expiresFontColor?: string;
  ffidCurated?: string;
  sectionBackColor?: string;
  orderedOn?: string;
  p2rStatus?: OrderStatusConfig;
  m2hStatus?: OrderStatusConfig;
}

/**
 * Map an Order object returned by useOrderFetchFilter into the TileType shape
 * expected by the shared Tile component.
 */
const mapOrderToTile = (
  order: Order,
  orderedOn?: string,
  p2rStatus?: OrderStatusConfig,
  m2hStatus?: OrderStatusConfig,
): TileProps['tile'] => {
  const mediaClipProjectId: string | undefined = order.lineItems?.[0]?.custom?.fields?.mediaClipProjectId;

  let imageSrc: string = '';

  const lineItem = order.lineItems?.[0];
  const imageMetadata = (lineItem as any)?.imageMetadata;
  if (!mediaClipProjectId) {
    imageSrc =
      imageMetadata?.urlThumb ||
      imageMetadata?.url ||
      imageMetadata?.projectThumbnailUrl ||
      imageMetadata?.urlMidRes ||
      lineItem?.variant?.images?.[0] ||
      '/placeholder.png';
  }

  const orderStatus = getLeastOrderStatus(
    order,
    classifyCartKind(lineItem?.distributionChannel?.key),
    p2rStatus,
    m2hStatus,
  );

  return {
    tileTitle: `${orderedOn ?? 'ordered on'}`,
    imageSrc,
    mediaClipProjectId: mediaClipProjectId,
    imageAlt: order.lineItems?.[0]?.name ?? '',
    activityDate: order.createdAt ? new Date(order.createdAt).toLocaleDateString() : '',
    tileSubtext: orderStatus ?? '',
    tileCta1Link: `/order-details/?orderId=${order.orderId}`,
    ideasCategoryFontSize: 12,
    ideasCategoryText: '',
    ideasCategoryTextColor: '',
    ideasCategoryIcon: { src: '', alt: '', width: 0, height: 0 } as any,
    tileTitleFontSize: 16,
    tileTitleFontSizeMobile: 14,
    tileTitleTextColor: '#111111',
  };
};

const PAST_ORDER_MAX_TILES = 4;

const PastOrder: React.FC<PastOrderProps> = ({ ...data }) => {
  const {
    mainHeading,
    subHeading,
    expiresInOrStatus,
    tileCtaText,
    ctaText,
    mainHeadingTextColor,
    mainHeadingFontweight,
    mainHeadingFontWeightMobile,
    mainHeadingPosition,
    mainHeadingFontsizeMobile,
    mainHeadingFontsize,
    subtextFontsize,
    subtextHeadingFontweight,
    subtextHeadingTextColor,
    subtextHeadingFontsizeMobile,
    ariaLabelOfPreviousButton,
    ariaLabelOfNextButton,
    tileTitleFontWeight,
    tileTextFontSize,
    autoRotation,
    autoRotationSpeed,
    expiresInOrStatusFontweight,
    expiresInOrStatusBgColor,
    expiresFontColor,
    ffidCurated,
    sectionBackColor,
    orderedOn,
    p2rStatus,
    m2hStatus,
  } = data;

  const { orders, loading: ordersLoading } = useOrderFetchFilter({ limit: PAST_ORDER_MAX_TILES });
  const { refreshImagesForOrders } = useRefreshImages();

  const [refreshedLineItemsByLineItemId, setRefreshedLineItemsByLineItemId] = useState<Record<string, any>>({});
  const router = useRouter();
  const { isTouchDevice } = useTouchDevice();
  const [, width] = useMediaQuery(mobile);
  const [isDesktopSize] = useMediaQuery(desktop);
  const isPureMobile = width < tablet;
  const [currentSlidesPerView, setCurrentSlidesPerView] = React.useState(0);

  const userName = useUserName();
  const processedSubHeading = subHeading?.replace('{{firstName}}', userName || '');

  const ordersWithRefreshedThumbnails = useMemo(() => {
    return (orders ?? []).map((order: Order) => {
      const firstLineItem = order.lineItems?.[0];
      const lineItemId = firstLineItem?.lineItemId;

      if (!firstLineItem || !lineItemId) {
        return order;
      }

      const refreshedFirstLineItem = refreshedLineItemsByLineItemId[lineItemId];

      if (!refreshedFirstLineItem) {
        return order;
      }

      return {
        ...order,
        lineItems: [
          {
            ...firstLineItem,
            ...refreshedFirstLineItem,
            imageMetadata: {
              ...firstLineItem.imageMetadata,
              ...refreshedFirstLineItem.imageMetadata,
            },
          },
          ...(order.lineItems ?? []).slice(1),
        ],
      };
    });
  }, [orders, refreshedLineItemsByLineItemId]);

  const filteredTiles = ordersWithRefreshedThumbnails
    .slice(0, PAST_ORDER_MAX_TILES)
    .map((order) => mapOrderToTile(order, orderedOn, p2rStatus, m2hStatus));

  const categoryFontweightClass = tileTitleFontWeight && clsx([fontStyleMap[tileTitleFontWeight]]);
  const mainHeadingFontweightClass =
    mainHeadingFontweight &&
    clsx([fontStyleMap[(isPureMobile ? mainHeadingFontWeightMobile : mainHeadingFontweight) ?? 'regular']]);
  const ctaCardTextFontweightClass = clsx([
    fontStyleMap[
      isPureMobile ? (ctaText?.[0]?.ctaTextFontWeightMobile ?? '400') : (ctaText?.[0]?.ctaTextFontWeight ?? '400')
    ],
  ]);

  const deskSlidesPerView = `${filteredTiles && filteredTiles.length >= 1 ? filteredTiles.length : 1}`;
  const isSingleCard = filteredTiles?.length === 1;
  const orderCount = filteredTiles.length;
  const hasOrderOverflow = orderCount > currentSlidesPerView;

  const flexColClasses = 'flex flex-col gap-12 sm:gap-16 md:gap-24';

  useEffect(() => {
    if (ordersLoading || !orders?.length) return;

    const firstLineItems = orders
      .map((order: Order) => order.lineItems?.[0])
      .filter((lineItem): lineItem is LineItem => Boolean(lineItem));

    if (!firstLineItems.length) return;

    let cancelled = false;

    const refreshOrderThumbnails = async () => {
      try {
        const { refreshedLineItems } = await refreshImagesForOrders(firstLineItems);

        if (cancelled || !refreshedLineItems?.length) return;

        const nextRefreshedLineItemsByLineItemId = refreshedLineItems.reduce(
          (acc: Record<string, any>, lineItem: any) => {
            const lineItemId = lineItem?.lineItemId;

            if (lineItemId) {
              acc[lineItemId] = lineItem;
            }

            return acc;
          },
          {},
        );

        setRefreshedLineItemsByLineItemId((prev: Record<string, any>) => ({
          ...prev,
          ...nextRefreshedLineItemsByLineItemId,
        }));
      } catch (error) {
        console.error('Error refreshing order thumbnail images', error);
      }
    };

    void refreshOrderThumbnails();

    return () => {
      cancelled = true;
    };
  }, [ordersLoading, orders, refreshImagesForOrders]);

  return (
    <div className={`mx-20 mt-24 sm:mx-24 md:mx-75 lg:mx-76 xl:mx-0`}>
      <div
        data-testid="curated-slider"
        className={`curated-slider past-order container relative rounded-2xl ${!filteredTiles || (filteredTiles.length <= 0 && 'hidden')} heading-${mainHeadingPosition} pb-0`}
        style={{ backgroundColor: sectionBackColor ?? 'transparent' }}
        data-ffid={`ctr-${cleanString(ffidCurated ?? '')}-curated-slider`}
      >
        <div className={`${flexColClasses} px-8 pb-16 pt-24 sm:pb-24 md:py-32`}>
          <div className={`${flexColClasses} px-8`}>
            <div className={`flex ${positionMap[mainHeadingPosition ?? 'center']}`}>
              <h2
                className={`leading-[1.2] ${mainHeadingFontweightClass}`}
                style={{
                  color: mainHeadingTextColor,
                  ...(isDesktopSize
                    ? { fontSize: `${mainHeadingFontsize}px` }
                    : { fontSize: `${mainHeadingFontsizeMobile}px` }),
                }}
                data-ffid={`lbl-${cleanString(ffidCurated ?? '')}-main-heading`}
              >
                {mainHeading}
              </h2>
            </div>
            <div className={`flex ${positionMap[mainHeadingPosition ?? 'center']}`}>
              <h3
                style={{
                  fontSize: `${subtextFontsize}px`,
                  fontWeight: `${subtextHeadingFontweight}`,
                  color: subtextHeadingTextColor,
                  ...(isPureMobile && { fontSize: `${subtextHeadingFontsizeMobile}px` }),
                }}
                data-ffid={`lbl-${cleanString(ffidCurated ?? '')}-sub-heading`}
              >
                {processedSubHeading}
              </h3>
            </div>
          </div>
          {ordersLoading ? (
            <div className="flex w-full justify-center py-48" data-testid="past-order-loader" data-ffid="ctr-loader">
              <div className="size-40 animate-spin rounded-full border-4 border-[#01916D] border-t-transparent"></div>
            </div>
          ) : (
            <div>
              <div
                className={`relative flex items-stretch`}
                data-ffid={`ctr-${cleanString(ffidCurated ?? '')}-slider-content`}
              >
                <div
                  className={`w-full ${+deskSlidesPerView <= 2 ? 'two-slides' : 'more-slides'} bleed-right`}
                  data-ffid={`ctr-${cleanString(ffidCurated ?? '')}-tiles-container`}
                >
                  <Slider
                    a11y={{
                      enabled: true,
                      prevSlideMessage: ariaLabelOfPreviousButton,
                      nextSlideMessage: ariaLabelOfNextButton,
                    }}
                    pagination={
                      hasOrderOverflow
                        ? {
                            clickable: false,
                            dynamicBullets: false,
                          }
                        : false
                    }
                    onInit={(sw) => setFFIDsForPagination(sw, ffidCurated as string)}
                    onBreakpoint={(sw) => setCurrentSlidesPerView(sw.params.slidesPerView as number)}
                    aria-label={`${mainHeading?.trim() || ''}`}
                    uniqueFFIDText={ffidCurated}
                    dots={false}
                    arrows={hasOrderOverflow && !isTouchDevice}
                    arrowsClassName="z-20 bottom-0 mr-16"
                    ariaLabelOfPreviousButton={ariaLabelOfPreviousButton}
                    ariaLabelOfNextButton={ariaLabelOfNextButton}
                    allowTouchMove={true}
                    autoplay={
                      autoRotation && autoRotationSpeed
                        ? {
                            delay: autoRotationSpeed * 1000,
                            disableOnInteraction: true,
                            pauseOnMouseEnter: true,
                          }
                        : false
                    }
                    slidesOffsetBefore={16}
                    slidesOffsetAfter={hasOrderOverflow ? 0 : 16}
                    centerInsufficientSlides
                    breakpoints={{
                      0: {
                        spaceBetween: 12,
                        slidesPerView: 1.2,
                        centeredSlides: true,
                        centeredSlidesBounds: true,
                      },
                      [smallMobile]: {
                        slidesPerView: 1.4,
                      },
                      [mobile]: {
                        spaceBetween: 16,
                        slidesPerView: 2.2,
                        centeredSlides: false,
                        centeredSlidesBounds: false,
                      },
                      [desktop]: {
                        slidesPerView: 3.2,
                      },
                      [mediumDesktop]: {
                        spaceBetween: 20,
                        slidesPerView: 4,
                      },
                    }}
                    navPosition={mainHeadingPosition === 'right' ? 'left' : 'right'}
                  >
                    {filteredTiles
                      ? filteredTiles
                          .map((tile, index) => (
                            <button
                              key={tile.productKey ?? index}
                              onClick={() => {
                                router.push(tile.tileCta1Link);
                              }}
                              className="m-0 w-full cursor-pointer appearance-none border-none bg-transparent p-0 text-left"
                            >
                              <Tile
                                ffidCurated={ffidCurated}
                                expiresFontColor={expiresFontColor}
                                isSingleCard={isSingleCard}
                                key={'filteredTiles' + index}
                                keyProp={index}
                                tile={tile}
                                tileCtaText={tileCtaText}
                                backImage={false}
                                backgroundEntity={'transparent'}
                                expiresInOrStatus={expiresInOrStatus as string}
                                categoryFontweightClass={categoryFontweightClass}
                                showStatus={true}
                                showExpiresIn={true}
                                expiresInOrStatusFontweight={expiresInOrStatusFontweight}
                                expiresInOrStatusBgColor={expiresInOrStatusBgColor}
                                showSaveToProjects={false}
                                tileTextFontSize={tileTextFontSize}
                                sectionClass={'past-order'}
                              />
                            </button>
                          ))
                          .filter(Boolean)
                      : false}
                  </Slider>
                </div>
              </div>
            </div>
          )}
          <CuratedCtaCard
            ffidCurated={ffidCurated}
            ctaText={ctaText}
            ctaCardTextFontweightClass={ctaCardTextFontweightClass}
            sectionName={'past-order'}
            hideSection={!filteredTiles || filteredTiles.length > 3}
          />
        </div>
      </div>
    </div>
  );
};

export default PastOrder;
